import {useState} from 'react';
import {trpc, createMutationErrorHandler} from '@common-web/lib/trpc';
import {toast} from '@common-web/hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@common-web/components/ui/alert-dialog';
import {Loader2} from 'lucide-react';
import {Skeleton} from '@common-web/components/ui/skeleton';

type RoleScope = 'INTERNAL' | 'CLIENT' | 'GROUP';

/**
 * Role reassignment control for the internal user detail page. Renders a
 * select of assignable roles for the user's scope; picking a new role opens
 * a confirmation dialog (the change signs the user out everywhere).
 *
 * When the viewer lacks `user_management.edit_user_role` (the
 * `roles.listAssignable` query 403s) the current role renders as a
 * read-only badge instead, so the role stays visible.
 *
 * `clientId` is the target user's tenant — custom roles are tenant-owned, so
 * it must be passed for CLIENT/GROUP scopes to offer that tenant's custom
 * roles alongside the global SYSTEM catalog.
 */
export function ChangeUserRole({
  userId,
  userName,
  scope,
  clientId,
  currentRole,
  disabled,
  disabledReason,
  onRoleChanged,
}: {
  userId: string;
  userName: string;
  scope: RoleScope;
  clientId?: string;
  currentRole: string;
  disabled?: boolean;
  disabledReason?: string;
  onRoleChanged: () => void;
}) {
  const [pendingRole, setPendingRole] = useState<{key: string; name: string} | null>(null);

  const {data: assignableRoles, isLoading} = trpc.internal.roles.listAssignable.useQuery(
    {scope, clientId: clientId || undefined},
    {retry: false},
  );

  const setUserRole = trpc.internal.userManagement.setUserRole.useMutation({
    onSuccess: (res) => {
      toast({
        title: 'Role updated',
        description: `${userName} is now ${res.roleName}. ${res.sessionsInvalidated} session(s) signed out.`,
      });
      setPendingRole(null);
      onRoleChanged();
    },
    onError: (err) => {
      createMutationErrorHandler('Error updating role')(err);
      setPendingRole(null);
    },
  });

  if (isLoading) {
    return <Skeleton className='h-9 w-56' />;
  }

  if (!assignableRoles || assignableRoles.length === 0) {
    return (
      <div
        className='inline-flex w-56 items-center rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-brand-navy'
        title='You do not have permission to change this user’s role'
      >
        {currentRole}
      </div>
    );
  }

  const currentRoleName = assignableRoles.find((r) => r.key === currentRole)?.name ?? currentRole;

  return (
    <>
      <Select
        value={currentRole}
        disabled={disabled || setUserRole.isPending}
        onValueChange={(value) => {
          if (value === currentRole) return;
          const role = assignableRoles.find((r) => r.key === value);
          if (role) setPendingRole({key: role.key, name: role.name});
        }}
      >
        <SelectTrigger className='w-56' title={disabled ? disabledReason : undefined}>
          <SelectValue placeholder={currentRoleName} />
        </SelectTrigger>
        <SelectContent>
          {assignableRoles.map((role) => (
            <SelectItem key={role.key} value={role.key}>
              {role.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <AlertDialog open={!!pendingRole} onOpenChange={(open) => !open && setPendingRole(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change role for {userName}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will change their role from{' '}
              <span className='font-medium text-brand-navy'>{currentRoleName}</span> to{' '}
              <span className='font-medium text-brand-navy'>{pendingRole?.name}</span>. They will be
              signed out everywhere and get the new role's access the next time they sign in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={setUserRole.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (pendingRole) {
                  setUserRole.mutate({userId, role: pendingRole.key});
                }
              }}
              disabled={setUserRole.isPending}
              className='bg-brand-blue hover:bg-brand-blue-dark text-white'
            >
              {setUserRole.isPending ? (
                <>
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                  Updating...
                </>
              ) : (
                'Change Role'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
